import {transform, map, flatMap, uniq, without, cloneDeep, values, uniqBy} from 'lodash';
import {FormFragmentSchema} from 'apstra-ui-common';

import {Probe, ProbeProcessor, ProcessorDefinition, StageValueType} from '../types';
import {
  getOutputNameByStageName, getProcessorByStageName, getPossibleStageValues,
  convertValueType,
  getStageKeyLabels
} from '../stageUtils';
import humanizeString from '../../humanizeString';

export const filterSectionsSchema = {
  key: {
    title: 'Keys',
    columnTitles: ['Include', 'Include As Context', 'Exclude'],
    values: ['include', 'include_as_static_context_key', 'exclude'],
    defaultValue: 'include',
  },
  value: {
    title: 'Values',
    columnTitles: ['Include', 'Exclude'],
    values: ['include', 'exclude'],
    defaultValue: 'exclude',
  },
};

export type ColumnFilterColumnSchema = {
  name: string;
  title?: string;
  description?: string;
  type?: string | string[];
};

export type ColumnFilterSchema = {
  key: ColumnFilterColumnSchema[];
  value: ColumnFilterColumnSchema[];
};

export type ColumnFilterValue = {
  key: Record<string, 'include' | 'include_as_static_context_key' | 'exclude'>;
  value: Record<string, 'include' | 'exclude'>;
};

export const buildColumnFilterSchema = (
  probe: Probe, inputStageName, inputColumnName, processorDefinitionsByName
): ColumnFilterSchema => {
  const result: ColumnFilterSchema = {key: [], value: []};
  if (inputStageName) {
    const sourceProcessor = getProcessorByStageName({probe, stageName: inputStageName});
    if (sourceProcessor) {
      const sourceProcessorDefinition = processorDefinitionsByName[sourceProcessor.name];
      const sourceOutputName = getOutputNameByStageName({processor: sourceProcessor, stageName: inputStageName});
      if (sourceProcessorDefinition && sourceOutputName) {
        const stageDefinition = sourceProcessorDefinition.outputs[sourceOutputName];
        const stageKeys = uniqBy(
          flatMap(
            stageDefinition?.types,
            ({keys}) => map(keys, (type, name) => ({name, type}))
          ),
          'name'
        );
        const stageKeyLabels = getStageKeyLabels({
          probe,
          processor: sourceProcessor,
          processorDefinitions: values(processorDefinitionsByName),
          keyNames: map(stageKeys, 'name')
        });
        result.key = map(stageKeys, ({name, type}) => ({
          name,
          type,
          title: stageKeyLabels[name] ?? humanizeString(name),
        }));
        result.value = flatMap(
          transform(
            getPossibleStageValues(stageDefinition),
            (result, {name, title, description, type}) => {
              if (inputColumnName === '*' || name === inputColumnName) {
                if (result[name]) {
                  result[name].type.push(type);
                } else {
                  result[name] = {name, title, description, type: type ? [type] : []};
                }
              }
            },
            {} as Record<string, ColumnFilterColumnSchema & {type: string[]}>
          ),
          (column) => {
            const types = uniq(map(column.type, (type) => convertValueType(type as StageValueType)));
            const type = types.length === 0 ? undefined : types.length === 1 ? types[0] : types;
            return {...column, type};
          }
        );
      }
    }
  }
  return result;
};

export const buildSeriesFilterCompleterParams = (
  probe: Probe, inputStageName, inputColumnName, processorDefinitionsByName
) => {
  const filterSchema = buildColumnFilterSchema(probe, inputStageName, inputColumnName, processorDefinitionsByName);
  const completerSchema = transform(filterSchema, (result, columns, sectionName) => {
    result.push(...columns.map(({name, type, title, description}) => ({
      name,
      schema: {type, title, description},
      meta: sectionName
    })));
  }, [] as FormFragmentSchema);
  return {schema: completerSchema, excludeOperators: ['is', 'is not']};
};

export const normalizeColumnFilterValue = (value: ColumnFilterValue, filterSchema): ColumnFilterValue => {
  return transform(filterSectionsSchema, (result, {values, defaultValue}, sectionName) => {
    const sensibleValues = new Set(without(values, defaultValue));
    result[sectionName] = transform(filterSchema[sectionName], (result, {name}) => {
      const currentValue = value[sectionName][name];
      if (sensibleValues.has(currentValue)) result[name] = currentValue;
    }, {} as ColumnFilterValue[keyof ColumnFilterValue]);
  }, {} as ColumnFilterValue);
};

export const filterProcessorDefinitionTypes = (
  processorDefinition: ProcessorDefinition,
  probe: Probe,
  processor: ProbeProcessor,
  processorDefinitionsByName: Record<string, ProcessorDefinition>
) => {
  processorDefinition = cloneDeep(processorDefinition);
  // always remove the original types in case the further calcluation fails
  processorDefinition.outputs.out.types = [];
  const {stage: inputStageName, column: inputColumnName} = processor.inputs.in ?? {};
  const filterSchema = buildColumnFilterSchema(probe, inputStageName, inputColumnName, processorDefinitionsByName);
  const filterValue = normalizeColumnFilterValue(processor.properties.column_filter, filterSchema);
  if (inputStageName) {
    const sourceProcessor = getProcessorByStageName({probe, stageName: inputStageName});
    if (sourceProcessor) {
      const sourceProcessorDefinition = processorDefinitionsByName[sourceProcessor.name];
      const sourceOutputName = getOutputNameByStageName({processor: sourceProcessor, stageName: inputStageName});
      if (sourceProcessorDefinition && sourceOutputName) {
        const sourceStageDefinition = sourceProcessorDefinition.outputs[sourceOutputName];
        for (const type of sourceStageDefinition.types) {
          const filteredType = {...type};
          for (const [filterSectionName, typeSectionName] of [['key', 'keys'], ['value', 'values']]) {
            filteredType[typeSectionName] = transform(type[typeSectionName], (result, value, key) => {
              if ((
                filterValue[filterSectionName][key] ??
                filterSectionsSchema[filterSectionName].defaultValue
              ) !== 'exclude') result[key] = value;
            }, {});
          }
          processorDefinition.outputs.out.types.push(filteredType);
        }
      }
    }
  }
  return processorDefinition;
};
